import cx from "clsx";
import SizingContainer from "common-utils/components/SizingContainer";
import { Artwork, FileHandle } from "kreate-common/modules/business-types";
import ButtonOutline from "kreate-common/modules/kreate-ui/components/ButtonOutline";
import ButtonPrimary from "kreate-common/modules/kreate-ui/components/ButtonPrimary";
import ButtonTransparentV2 from "kreate-common/modules/kreate-ui/components/ButtonTransparentV2";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import ModalV2 from "kreate-common/modules/kreate-ui/components/ModalV2";
import Popover from "kreate-common/modules/kreate-ui/components/Popover";
import React from "react";
import { MdInfoOutline } from "react-icons/md";

import Alert from "../ModalEnableMinting$Prompt/components/Alert";

import styles from "./index.module.scss";

import { formatLovelaceAmount$Standard } from "@/modules/common-utils/format";
import { ImageViewer } from "@/modules/kreate-components/components/ImageViewer";

export type Row = {
  label: React.ReactNode;
  lovelaceAmount: number | null | undefined;
  tooltip: React.ReactNode;
};

type Props = {
  open: boolean;
  rows: Row[];
  /** @deprecated use `imageFile` instead */
  artwork?: Artwork | null | undefined;
  imageFile?: FileHandle | null | undefined;
  customImage?: React.ReactNode;
  description?: React.ReactNode;
  onAccept?: () => void;
  onReject?: () => void;
};

export default function ModalTransactionBreakdown({
  open,
  rows,
  artwork,
  imageFile = artwork?.file || artwork?.imageFile,
  customImage,
  description,
  onAccept,
  onReject,
}: Props) {
  const totalLovelaceAmount = (() => {
    let result = 0;
    for (const row of rows) {
      result += row.lovelaceAmount ?? 0;
    }
    return result;
  })();

  return (
    <ModalV2
      open={open}
      onClose={onReject}
      closeOnDimmerClick={false}
      closeOnEscape={false}
    >
      <ModalV2.Header content="Transaction Breakdown" />
      <ModalV2.Content>
        <Flex.Row gap="20px">
          {imageFile ? (
            <Flex.Cell flex="0 0 140px">
              <SizingContainer
                className={styles.imageContainer}
                sizing={{ type: "auto-height", aspectRatio: imageFile }}
              >
                <ImageViewer
                  value={imageFile}
                  sizing="fill"
                  preventAnimation
                  sizes="140px"
                />
              </SizingContainer>
            </Flex.Cell>
          ) : customImage ? (
            <Flex.Cell flex="0 0 140px">{customImage}</Flex.Cell>
          ) : undefined}
          <Flex.Col flex="1 1 auto" gap="12px" minWidth="0">
            <Flex.Col
              className={styles.details}
              gap="16px"
              padding="32px 24px 24px 24px"
            >
              {rows.map((row, index) => (
                <Flex.Row key={index} justifyContent="space-between">
                  <Flex.Cell className={styles.label}>{row.label}</Flex.Cell>
                  <Flex.Row
                    className={styles.value}
                    alignItems="center"
                    justifyContent="flex-end"
                    gap="8px"
                  >
                    <Flex.Cell>
                      {formatLovelaceAmount$Standard(row.lovelaceAmount)}
                    </Flex.Cell>
                    <Flex.Cell minWidth="20px" maxWidth="20px">
                      {row.tooltip ? (
                        <Popover
                          trigger={
                            <ButtonTransparentV2 className={styles.buttonInfo}>
                              <MdInfoOutline size="20px" />
                            </ButtonTransparentV2>
                          }
                        >
                          {row.tooltip}
                        </Popover>
                      ) : undefined}
                    </Flex.Cell>
                  </Flex.Row>
                </Flex.Row>
              ))}
              <hr className={styles.divider} />
              <Flex.Row alignItems="baseline" justifyContent="space-between">
                <Flex.Cell className={cx(styles.label, styles.labelEmphasis)}>
                  {"Total"}
                </Flex.Cell>
                <Flex.Cell className={cx(styles.value, styles.valueEmphasis)}>
                  {formatLovelaceAmount$Standard(totalLovelaceAmount)}
                </Flex.Cell>
              </Flex.Row>
            </Flex.Col>
            {description ? (
              <Alert icon={<MdInfoOutline />} content={description} />
            ) : undefined}
          </Flex.Col>
        </Flex.Row>
      </ModalV2.Content>
      <ModalV2.Actions>
        <ButtonOutline content="Cancel" onClick={onReject} />
        <ButtonPrimary content="Confirm" onClick={onAccept} />
      </ModalV2.Actions>
    </ModalV2>
  );
}
